/**
 * The complete OVERVOLT game state.
 *
 * One plain object holds the whole run. The simulation mutates it in place;
 * the renderer, HUD and the `window.__GAME__` snapshot only read it.
 */
import { CONFIG, type AbilityId, type Difficulty, type TowerType, type WaveDef } from '../config';
import type { Enemy, Particle, Projectile, Tower } from './entities';
import { PATH, PATH_LENGTH, activeMapId, type Cell } from './map';

/** The OVERVOLT phase machine — see docs/GAME_DESIGN.md §2. */
export type GamePhase = 'build' | 'wave' | 'won' | 'lost';

/**
 * A run's mode — Campaign (10 waves, winnable), Endless (procedural, no win),
 * Maze (open-field route-building, §16), or Puzzle (Sprint 30 — hand-authored
 * fixed-path levels with budgets, allowed towers, and impenetrable LOS walls).
 * Campaign / Endless / Maze play the 10-wave Campaign table; Campaign / Maze
 * win at wave 10, Endless never wins. Puzzle runs a single authored wave per
 * level and wins on `enemies.length === 0` once the wave director is done.
 */
export type GameMode = 'campaign' | 'endless' | 'maze' | 'puzzle';

/** Power-grid readout, recomputed every tick. */
export interface PowerState {
  /** Total supply: reactor base + pylons. */
  supply: number;
  /** Total demand: sum of weapon-tower power draw. */
  demand: number;
  /** clamp(supply / max(demand, 1), 0, 1). Below 1 means brownout. */
  efficiency: number;
  /**
   * `supply − demand` — signed grid headroom (negative is a brownout). Where
   * `efficiency` clamps at 1.0, `surplus` measures how far the grid runs rich;
   * the Arc Coil's damage + chain reach couple to it (docs/GAME_DESIGN.md §5).
   */
  surplus: number;
  /**
   * Count of weapon towers currently in **OVERCLOCK** (REC 3, Sprint 26 —
   * docs/REC3_DESIGN.md §4.1.2). Pure derived sum — recomputed in
   * `recomputePower`. Exposed for the HUD's `OC ×N` pip and QA assertions;
   * `0` when no tower is Overclocked, so a Sprint-25 baseline run never sees
   * a non-zero readout.
   */
  overclockCount: number;
}

/** Progress tracking for the active wave's spawn schedule. */
export interface WaveDirector {
  /** Index into the current wave's `groups`. */
  groupIndex: number;
  /** Enemies already spawned from the current group. */
  spawnedInGroup: number;
  /** Ticks until the next spawn is allowed. */
  cooldown: number;
  /** True once every group of the wave has been fully spawned. */
  done: boolean;
}

/** A transient HUD message (e.g. "Not enough gold"). */
export interface Notice {
  text: string;
  /** Ticks of life remaining. */
  ttl: number;
}

/**
 * One placed Airstrike **scorch zone** — the lingering kill-zone half of the
 * Sprint 20 Airstrike re-role (docs/GAME_DESIGN.md §15). Tile-anchored so it
 * round-trips cleanly through `runSave` (a JSON-clean record of integers);
 * `radius` is captured at placement time so a later config change cannot
 * retroactively resize a zone in flight.
 */
export interface ScorchZone {
  /** Tile column the zone is centred on (the Airstrike target tile). */
  col: number;
  /** Tile row the zone is centred on. */
  row: number;
  /** Ticks of life remaining; the zone is dropped from the list at 0. */
  ticksLeft: number;
  /** Zone radius in tiles (captured at placement). */
  radius: number;
}

/**
 * Live per-run state of one active ability — see docs/GAME_DESIGN.md §15.
 * `cost` / `cooldownTicks` mirror `config.abilities`; `cooldownLeft` is the
 * runtime charge counter (counts down only during the wave phase).
 */
export interface AbilityRuntime {
  /** Stable id — matches `config.abilities[*].id`. */
  id: AbilityId;
  /** Human-readable name for the HUD. */
  name: string;
  /** Gold cost per use. */
  cost: number;
  /** Cooldown in ticks after a use. */
  cooldownTicks: number;
  /** Ticks until the ability may be used again (0 = ready). */
  cooldownLeft: number;
  /** True when off cooldown (`cooldownLeft === 0`). */
  ready: boolean;
}

/** The full, serialisable run state. */
export interface GameState {
  phase: GamePhase;
  /** Campaign (10-wave, winnable), Endless (procedural, no win) or Maze. */
  mode: GameMode;
  /** Difficulty mode for this run (Easy / Normal / Hard). */
  difficulty: Difficulty;
  /** PRNG seed for this run — also seeds Endless wave generation. */
  seed: number;
  /**
   * Id of the active map for this run — selected before the run starts (URL
   * `?map=<id>`, the title-screen chip, or `__GAME__.setMap`). The active
   * map's path + blocked-tile set drives the simulation via the live `map.ts`
   * bindings; this field round-trips the choice through the run save and the
   * snapshot. See docs/GAME_DESIGN.md §3.
   */
  mapId: string;
  /** Current/last wave number (0 before the first wave starts). */
  wave: number;
  /**
   * Player-set Overload tier (0–4) for the *next* wave — see §8. Sticky:
   * persists across waves; freely editable in any build phase.
   */
  overload: number;
  /** Overload tier locked in for the wave currently in progress. */
  waveOverload: number;
  /**
   * Highest Overload tier locked in across every wave of this run — recorded
   * into the Scoreboard run history at run end (§20.1). Run state, not meta:
   * a deterministic function of the wave-by-wave inputs.
   */
  overloadMax: number;
  gold: number;
  lives: number;
  /** Starting lives for this run (difficulty-resolved) — drives the flawless flourish. */
  startLives: number;
  score: number;
  /**
   * Settlement baseline for a continued run (Continue → Endless, §14). When a
   * `won` run is continued into Endless the winning campaign/maze segment has
   * **already** been settled into the profile (§19.5), so a later settlement of
   * the Endless continuation must count only its OWN contribution. These hold
   * the waves cleared and the raw `score` already settled by that prior
   * segment; both are `0` for a fresh, never-continued run. Run state, not meta
   * — a deterministic function of the player's Continue input.
   */
  settledWaves: number;
  settledScore: number;
  /** Final score, computed once when the run ends. */
  finalScore: number;
  /** Best score persisted across runs for the current mode (mirrors localStorage). */
  highScore: number;
  /** Deepest Endless wave reached — this run vs. the persisted record. */
  bestWave: number;
  /** Logical ticks elapsed in the current run. */
  tick: number;
  /** True while the simulation is frozen by the player. */
  paused: boolean;
  /** Simulation speed multiplier: 1 or 2. */
  speed: 1 | 2;
  /** Whether audio is muted. */
  muted: boolean;

  enemies: Enemy[];
  towers: Tower[];
  projectiles: Projectile[];
  particles: Particle[];

  /**
   * The live global spawn→Core route (a tile polyline). On a fixed-path map it
   * is the hard-coded S-path; in Maze Mode it is the A* route over the current
   * blocked set, recomputed on every tower place/sell (docs §16.3).
   */
  path: readonly Cell[];
  /** Tile count of the global route minus one — `state.path.length − 1`. */
  mazePathLength: number;

  /**
   * Sprint 30 — puzzle-mode **line-of-sight walls** (packed tile indices,
   * `row * cols + col`). When non-null, each entry is an impenetrable wall
   * tile: not buildable, not path-walkable, and **blocks line-of-fire** for
   * every direct-fire tower (the Mortar's parabolic arc is exempt — see
   * docs/PUZZLE_DESIGN.md §2.4). `null` outside puzzle mode — the LOS test in
   * `los.ts` short-circuits to a single null-check, so Campaign / Endless /
   * Maze pay zero measurable cost. See docs/PUZZLE_DESIGN.md §2.
   *
   * The set IS the encoding of the wall mask; the wall tiles themselves are
   * also registered in the active map's `blocked` list (so placement / path
   * rejects them through the existing `isBlockedTile` path — no systems.ts
   * edits needed for placement gating).
   */
  losWalls: ReadonlySet<number> | null;
  /**
   * Sprint 30 — puzzle-mode active level id (matches `PuzzleLevel.id` in
   * `puzzles.ts`). `null` outside puzzle mode. Mirrored into the snapshot so
   * QA can verify the loaded level deterministically.
   */
  puzzleLevelId: string | null;
  /**
   * Sprint 63 (puzzle-ux-briefing-s63) — true while the **briefing card** for
   * the loaded puzzle level should sit on top of the playfield (player has
   * not pressed Start yet). Set to `true` by `loadPuzzleLevel` after each
   * level load; cleared by the orchestrator when the player presses Start.
   * `false` outside puzzle mode (the briefing card is a puzzle-only UI).
   *
   * Render-only signal — the simulation never reads this field; clicks are
   * absorbed by the briefing-card hit-test before they ever reach
   * `handlePlayfieldClick`, so the player cannot place towers underneath the
   * briefing. Mirrored into the snapshot so QA can step through the
   * briefing flow deterministically.
   */
  puzzleBriefingPending: boolean;

  /** Live state of the three mid-wave active abilities — see §15. */
  abilities: AbilityRuntime[];
  /** Ticks remaining in the active Overcharge burst window (0 = not active). */
  overchargeTicksLeft: number;
  /**
   * Ticks remaining in the **Overcharge aftershock brownout** (0 = no
   * aftershock active). The proactive cost of the burst: while this is > 0
   * the cooldown-scaling efficiency (§6) is *capped* at the ability's
   * `aftershockEfficiency`, so every tower fires slower than normal. Starts
   * the moment the burst window ends. See docs/GAME_DESIGN.md §15.
   */
  aftershockTicksLeft: number;
  /**
   * Live Airstrike **scorch zones** — placed lingering kill-zones, the
   * proactive re-role of the ability (docs/GAME_DESIGN.md §15). Each zone is
   * a tile-anchored disc of radius `radius` (tiles) that pulses `scorch
   * TickDamage × hpMultiplier(wave)` chip damage **and** refreshes a brittle
   * mark on every enemy inside it, every tick, until `ticksLeft` reaches 0.
   * Multiple zones may coexist (one per fire); each ages independently.
   */
  scorchZones: ScorchZone[];
  /**
   * Ticks remaining in an emitted Kernel Power Drain (0 = no drain active). A
   * live drain multiplies the grid's base efficiency by `bossDrainMult` — see
   * docs/GAME_DESIGN.md §17.3.
   */
  drainTicksLeft: number;
  /** Telegraph ticks left before the Kernel's charging Power Drain emits (0 = idle). */
  drainCharge: number;
  /** Boss-internal timer: ticks until the Kernel's next Power-Drain charge begins. */
  drainCooldown: number;

  power: PowerState;
  director: WaveDirector;
  /** Composition of the wave currently spawning (campaign table or generated). */
  activeWave: WaveDef;

  /** Tower type armed for placement, or null. */
  selectedTowerType: TowerType | null;
  /** Id of the inspected tower (shows range ring + Sell), or null. */
  selectedTowerId: number | null;

  /** Monotonic id source for enemies / towers / projectiles. */
  nextId: number;
  /** Transient HUD message, or null. */
  notice: Notice | null;
}

/** A finished wave director (nothing left to spawn). */
function idleDirector(): WaveDirector {
  return { groupIndex: 0, spawnedInGroup: 0, cooldown: 0, done: true };
}

/**
 * Resolved starting gold for a fresh run — the Normal base scaled by the
 * difficulty `startingGoldMult` (§8), plus the flat **Maze-mode early-economy
 * nudge** (`mazeStartGoldBonus`, §16.9) on a Maze run. The nudge lets a Maze
 * player afford to both wall the field and arm it in the opening waves; it is
 * mode-specific and difficulty-neutral, so every difficulty keeps its own
 * relative challenge. Pure function of `(difficulty, mode)` — determinism-safe.
 */
export function startingGoldFor(difficulty: Difficulty, mode: GameMode): number {
  const base = Math.round(CONFIG.startingGold * CONFIG.difficulties[difficulty].startingGoldMult);
  return mode === 'maze' ? base + CONFIG.mazeStartGoldBonus : base;
}

/**
 * Build a fresh run state, parked in the build phase before wave 1. Starting
 * gold and lives are resolved from the difficulty multipliers — and, for a
 * Maze run, the §16.9 early-economy nudge — at this point.
 */
export function createInitialState(
  difficulty: Difficulty = 'normal',
  mode: GameMode = 'campaign',
): GameState {
  const d = CONFIG.difficulties[difficulty];
  const lives = Math.round(CONFIG.startingLives * d.startingLivesMult);
  return {
    phase: 'build',
    mode,
    difficulty,
    seed: CONFIG.seed,
    // The active map's id (snapped from the live `map.ts` binding); the caller
    // is expected to have called `setActiveMap(...)` first if a non-default map
    // was selected. See docs/GAME_DESIGN.md §3.
    mapId: activeMapId(),
    wave: 0,
    overload: 0,
    waveOverload: 0,
    overloadMax: 0,
    gold: startingGoldFor(difficulty, mode),
    lives,
    startLives: lives,
    score: 0,
    settledWaves: 0,
    settledScore: 0,
    finalScore: 0,
    highScore: 0,
    bestWave: 0,
    tick: 0,
    paused: false,
    speed: 1,
    muted: false,
    enemies: [],
    towers: [],
    projectiles: [],
    particles: [],
    // Fixed-path default; Maze Mode overwrites this via `recomputePaths`.
    path: PATH,
    mazePathLength: PATH_LENGTH,
    // Sprint 30 — puzzle mode initialises `losWalls` to a fresh set via
    // `loadPuzzleLevel`. Every other mode keeps it `null` (zero impact on the
    // live game). See docs/PUZZLE_DESIGN.md §2.5.
    losWalls: null,
    puzzleLevelId: null,
    // Sprint 63 — `loadPuzzleLevel` flips this to `true` after each level load
    // (briefing card not yet dismissed). Never set outside puzzle mode.
    puzzleBriefingPending: false,
    abilities: CONFIG.abilities.map((a) => ({
      id: a.id,
      name: a.name,
      cost: a.cost,
      cooldownTicks: a.cooldownTicks,
      cooldownLeft: 0,
      ready: true,
    })),
    overchargeTicksLeft: 0,
    aftershockTicksLeft: 0,
    scorchZones: [],
    drainTicksLeft: 0,
    drainCharge: 0,
    drainCooldown: 0,
    power: {
      supply: CONFIG.reactorBasePower,
      demand: 0,
      efficiency: 1,
      surplus: CONFIG.reactorBasePower,
      // Sprint 26 (REC 3): no towers placed yet, so 0 are Overclocked.
      overclockCount: 0,
    },
    director: idleDirector(),
    activeWave: { groups: [] },
    selectedTowerType: null,
    selectedTowerId: null,
    nextId: 1,
    notice: null,
  };
}
